import { useUser } from "@supabase/auth-helpers-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchIsLiked, fetchLikeCount, like, TParentType, unlike } from "@/lib/community";
import { MixpanelActions } from "../integrations/mixpanel/mixpanel";

export function useLike(parentId: number | string, parentType: TParentType) {
	const user = useUser();
	const userId = user?.id;
	const queryClient = useQueryClient();

	const { data: isLiked = false, isLoading: likeStatusLoading } = useQuery({
		queryKey: ['isLiked', parentId, userId],
		queryFn: () => fetchIsLiked(parentId, userId, parentType),
		enabled: !!parentId && !!userId,
	});

	const { data: likeCount = 0, isLoading: likeCountLoading } = useQuery({
		queryKey: ['likeCount', parentId],
		queryFn: () => fetchLikeCount(parentId, parentType),
		enabled: !!parentId,
	});

	const likeMutation = useMutation({
		mutationFn: () => {
			switch (parentType) {
				case 'comment':
					MixpanelActions.likeComment(parentId);
					break;
				case "post":
					MixpanelActions.likePost(parentId);
					break;

			}
			return like(parentId, userId, parentType);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['likeCount', parentId] });
			queryClient.invalidateQueries({ queryKey: ['isLiked', parentId, userId] });
		},
	});

	const unlikeMutation = useMutation({
		mutationFn: () => unlike(parentId, userId, parentType),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['likeCount', parentId] });
			queryClient.invalidateQueries({ queryKey: ['isLiked', parentId, userId] });
		},
	});

	const toggleLike = async () => {
		if (!userId) return;
		if (isLiked) {
			await unlikeMutation.mutateAsync();
		} else {
			await likeMutation.mutateAsync();
		}
	};

	return {
		isLiked,
		likeCount,
		loading: likeStatusLoading || likeCountLoading,
		toggleLike,
	};
}